import { mat3, ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { Observable } from '../core/observable';
import { ViewManager } from './ViewManager';
import { ViewUpdateEvent } from './events/ViewUpdateEvent';
import { LayerBit } from './layers/LayerBit';
import { PlaneView } from './views/PlaneView';
import { RotatedBoundingBox } from '../data/common/RotatedBoundingBox';
import { BoundingBox } from '../data/common/BoundingBox';

/**
 * {@link ViewUpdateEvent} that is generated as a result of pan and zoom events.\
 * {@link ViewUpdateEvent ViewUpdateEvents} of this type have {@link ViewUpdateEvent.affectedLayers affectedLayers} of {@link LayerBit.ALL_LAYERS}.
 * Meaning that all the layers will be updated.\
 * {@link ViewUpdateEvent.updateContent updateContent} is also set to true, so events of this type will always update the contents of all the layers, it's not just a post-processing update.
 */
export class PanZoomViewUpdateEvent extends ViewUpdateEvent {
    public constructor() {
        super(LayerBit.ALL_LAYERS, true);
    }

    /**
     * Method to see if this {@link PanZoomViewUpdateEvent} should update the {@link view}.\
     * {@link PanZoom} only affects {@link PlaneView PlaneViews}, so this only returns true if the {@link view} is an instance of {@link PlaneView}.
     * @param view The {@link View} that may be updated
     * @returns True if the {@link view} is an instanceof {@link PlaneView} and should be updated, false otherwise.
     */
    public shouldUpdateView(_view: PlaneView): boolean {
        return true;
    }

    /**
     * Method to update the {@link view} contents.
     * @param view {@link View} the contents of which are to be updated.
     * @returns True to indicate that the contents of the {@link view} were successfully updated.
     */
    public updateViewContent(view: PlaneView): boolean {
        view.updateViewContent(this.affectedLayers);
        return true;
    }
}

/**
 * {@link PanZoom} is used to apply additional translation and scaling to {@link PlaneView PlaneViews}.\
 * It can be used to easily manipulate the center coordinates and scaling of {@link PlaneView PlaneViews}.\
 * Changes made to {@link PanZoom._scale scale} and {@link PanZoom._translation translation} of the {@link PanZoom} send out
 * {@link PanZoomViewUpdateEvent} to {@link ViewManager} to update the {@link PlaneView PlaneViews} accordingly.
 */
export class PanZoom extends Observable<number> {
    /**
     * Multiplier controlling how much the scaling should change as a response to each {@link scaleIn}-method call.\
     * Generally it is good idea to have this as an inverse of {@link SCALE_OUT} so the scaling scales the same amount, just in the opposite direction.
     */
    private static readonly SCALE_IN = 1.15;

    /**
     * Multiplier controlling how much the scaling should change as a response to each {@link scaleOut}-method call.\
     * Generally it is good idea to have this as an inverse of {@link SCALE_IN} so the scaling scales the same amount, just in the opposite direction.
     */
    private static readonly SCALE_OUT = 1 / PanZoom.SCALE_IN;
    private _scale: number;

    /**
     * Boolean to determine if the zoom should be focused on the center of the view or the cursor position.\
     * Defaults to true.
     */
    private _zoomToCursor: boolean = true;

    /**
     * Constructor to create a new {@link PanZoom}-object.
     * It may be provided with initial {@link translation} and {@link scaling} values if so desired.
     * @param translation The initial {@link _translation} of this {@link PanZoom}-object. If undefined, vec3(0, 0, 0) will be used.
     * @param scaling The initial {@link _scale scale} of this {@link PanZoom}-object. Defaults to 1.
     */
    public constructor(scaling = 1) {
        super();
        this._scale = scaling;
    }

    /**
     * Core focus implementation — fits all views to the bounding box and applies zoom.
     * @param intersectionPoint The exact patient-space point to set as crosshair position.
     * @param snapToGrid Whether to snap the crosshair to the primary image voxel grid.
     */
    private applyFocus(
        mainView: PlaneView,
        coronalView: PlaneView,
        sagittalView: PlaneView,
        bb: RotatedBoundingBox,
        intersectionPoint: ReadonlyVec3,
        snapToGrid: boolean
    ): void {
        if (bb.volume <= 0) {
            return;
        }

        const zoomChange = mainView.fitToView(bb);
        mainView.updatePlaneIntersectionPoint(intersectionPoint);
        if (snapToGrid) {
            mainView.snapToImagePixel();
        }

        coronalView.fitToView(bb);
        sagittalView.fitToView(bb);

        this._scale = zoomChange;
        mainView.resetCurrentScale(this._scale);
        coronalView.resetCurrentScale(this._scale);
        sagittalView.resetCurrentScale(this._scale);

        ViewManager.viewUpdateEvent(new PanZoomViewUpdateEvent());
    }

    /**
     * Focus all views on a rotated bounding box, snapping to primary image grid.
     */
    public focusTo(
        mainView: PlaneView,
        coronalView: PlaneView,
        sagittalView: PlaneView,
        bb: RotatedBoundingBox
    ): void {
        const centerInPatientCoords = vec3.transformMat3(
            vec3.create(),
            bb.center,
            bb.rotation
        );
        this.applyFocus(
            mainView,
            coronalView,
            sagittalView,
            bb,
            centerInPatientCoords,
            true
        );
    }

    /**
     * Focus all views on a specific patient-space point with a reasonable zoom level.
     * Does not snap to primary image grid, preserving the exact target position.
     */
    public focusToPoint(
        mainView: PlaneView,
        coronalView: PlaneView,
        sagittalView: PlaneView,
        point: ReadonlyVec3,
        focusSizeMm: number = 30
    ): void {
        const halfSize = focusSizeMm / 2;
        const min = vec3.fromValues(
            point[0] - halfSize,
            point[1] - halfSize,
            point[2] - halfSize
        );
        const max = vec3.fromValues(
            point[0] + halfSize,
            point[1] + halfSize,
            point[2] + halfSize
        );
        const bb = new RotatedBoundingBox(
            new BoundingBox(min, max),
            mat3.create()
        );
        this.applyFocus(mainView, coronalView, sagittalView, bb, point, false);
    }

    public get zoomToCursor(): boolean {
        return this._zoomToCursor;
    }

    public setZoomToCursor(zoomToCursor: boolean) {
        this._zoomToCursor = zoomToCursor;
    }

    public zoomIn(view: PlaneView, center: ReadonlyVec2 | undefined) {
        if (this._scale < 10) {
            const newScale = this.scale * PanZoom.SCALE_IN;
            if (this._zoomToCursor && center) {
                view.zoomTo(view.clientToCanvas(center), newScale);
            } else {
                view.updateZoomScale(newScale);
            }
            this.scale = newScale;
        }
    }

    public zoomOut(view: PlaneView, center: ReadonlyVec2 | undefined) {
        if (this._scale > 0.4) {
            const newScale = this.scale * PanZoom.SCALE_OUT;
            if (this._zoomToCursor && center) {
                view.zoomTo(view.clientToCanvas(center), newScale);
            } else {
                view.updateZoomScale(newScale);
            }
            this.scale = newScale;
        }
    }

    /**
     * Method to get the current {@link _scale scaling} of this {@link PanZoom}-object.
     */
    public get scale(): number {
        return this._scale;
    }

    public set scale(scaling: number) {
        this._scale = scaling;
        this.notify(scaling);
        ViewManager.viewUpdateEvent(new PanZoomViewUpdateEvent());
    }
    /**
     * Method to move the {@link _translation} by some {@link ReadonlyVec3 delta vector}.
     * This also sends a new {@link PanZoomViewUpdateEvent} of type {@link PanZoomViewUpdateEvent.PAN PAN} to {@link ViewManager}.
     * @param delta {@link ReadonlyVec3} that is to be added to {@link _translation translation}.
     * @returns The new {@link _translation}.
     */
    public moveCenter(delta: ReadonlyVec3): ReadonlyVec3 {
        ViewManager.viewUpdateEvent(new PanZoomViewUpdateEvent());
        return delta; // vec3.add(this._translation, this._translation, delta);
    }
}
